import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { lockBodyScroll, unlockBodyScroll } from "../core/scroll-lock";
import { adminUpdatePartnerConfig } from "../core/axios/endpoints";
import { SUBSIDIE_MODULES, MODULE_LABELS } from "../core/partner-subsidie";
import PriceScopeChoice, { PRICE_SCOPE_OPEN } from "./price-scope-choice";

// Portal-modal voor admins om een klant als BTW-partner te activeren of de
// partner-instellingen aan te passen. Vervangt het oude env-driven seed
// patroon - alle partner-configuratie verloopt vanaf nu via de Klanten-UI.

const slugify = (input) =>
  String(input ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const PUBLIC_BASE_URL = "https://subsidie2gether.nl";

const PartnerConfigModal = ({ isOpen, user, onClose, onSaved }) => {
  const existingBtwFee =
    user?.applicationFees?.btwBatterij ?? user?.btwPricePerApplication ?? 65;

  const [enabled, setEnabled] = useState(false);
  const [partnerSlug, setPartnerSlug] = useState("");
  const [partnerLogoUrl, setPartnerLogoUrl] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [btwPrice, setBtwPrice] = useState(65);
  // Subsidie-partnerflow: per regeling aan/uit met eigen prijs per aanvraag.
  const [partnerModules, setPartnerModules] = useState([]);
  const [fees, setFees] = useState({ eia: 250, eiaZon: 250, isde: 250 });
  const [priceScope, setPriceScope] = useState(PRICE_SCOPE_OPEN);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      unlockBodyScroll();
    };
  }, [isOpen, saving, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSaved(null);
      return;
    }
    setEnabled(!!user?.btwPartnerEnabled);
    setPartnerSlug(
      user?.partnerSlug ?? slugify(user?.bedrijfsnaam ?? user?.naam ?? ""),
    );
    setPartnerLogoUrl(user?.partnerLogoUrl ?? "");
    setBillingEmail(user?.billingEmail ?? user?.email ?? "");
    setBtwPrice(existingBtwFee);
    setPartnerModules(
      Array.isArray(user?.partnerModules)
        ? user.partnerModules.filter((m) => SUBSIDIE_MODULES.includes(m))
        : [],
    );
    setFees({
      eia: user?.applicationFees?.eia ?? 250,
      eiaZon: user?.applicationFees?.eiaZon ?? 250,
      isde: user?.applicationFees?.isde ?? 250,
    });
    setPriceScope(PRICE_SCOPE_OPEN);
    setError(null);
    setSaved(null);
  }, [isOpen, user, existingBtwFee]);

  // Alleen een prijs die al gold en nu anders is telt als wijziging; een
  // module die net aangezet wordt heeft nog geen aanvragen.
  const priceChanged =
    (enabled && !!user?.btwPartnerEnabled && Number(btwPrice) !== existingBtwFee) ||
    partnerModules.some(
      (m) =>
        (user?.partnerModules ?? []).includes(m) &&
        Number(fees[m]) !== (user?.applicationFees?.[m] ?? 250),
    );

  const hasSubsidieModule = partnerModules.length > 0;
  const anyPartner = enabled || hasSubsidieModule;

  const toggleModule = (m) =>
    setPartnerModules((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
    );

  const partnerUrl = useMemo(() => {
    if (!partnerSlug) return "";
    return `${PUBLIC_BASE_URL}/btw/p/${slugify(partnerSlug)}`;
  }, [partnerSlug]);

  const subsidiePartnerUrl = useMemo(() => {
    if (!partnerSlug) return "";
    return `${PUBLIC_BASE_URL}/subsidie/p/${slugify(partnerSlug)}`;
  }, [partnerSlug]);

  const submit = async () => {
    if (!user?._id) return;
    setSaving(true);
    setError(null);
    try {
      // Slug, logo en billing-e-mail gelden voor beide flows (BTW en
      // subsidie), dus die gaan ook mee als alleen subsidie-modules aan staan.
      const feesPayload = {};
      partnerModules.forEach((m) => {
        const v = Number(fees[m]);
        if (Number.isFinite(v) && v >= 0) feesPayload[m] = v;
      });
      const payload = {
        enabled,
        partnerModules,
        fees: feesPayload,
        ...(priceChanged ? { priceScope } : {}),
        ...(anyPartner
          ? {
              partnerSlug: slugify(partnerSlug),
              partnerLogoUrl: partnerLogoUrl || undefined,
              billingEmail: billingEmail || undefined,
            }
          : {}),
        ...(enabled
          ? {
              btwPriceExclVat:
                typeof btwPrice === "number" && btwPrice >= 0
                  ? btwPrice
                  : undefined,
            }
          : {}),
      };
      const res = await adminUpdatePartnerConfig(user._id, payload);
      const updatedUser = res?.data ?? res;
      setSaved(updatedUser);
      onSaved?.(updatedUser);
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Opslaan mislukt.",
      );
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async (url = partnerUrl) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // clipboard kan geblokkeerd zijn - dan is de URL handmatig kopieerbaar.
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const customerName =
    user?.bedrijfsnaam || user?.naam || user?.email || "Klant";

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-6">
          <div>
            <h2 className="font-display text-xl text-gray-900">
              Partner instellen
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              <strong>{customerName}</strong>{" "}
              {anyPartner
                ? `wordt partner voor ${[
                    enabled ? "BTW Batterij" : null,
                    ...partnerModules.map((m) => MODULE_LABELS[m]),
                  ]
                    .filter(Boolean)
                    .join(", ")}.`
                : "is geen partner."}{" "}
              Partners zien in hun portaal automatisch de bijbehorende overzichten.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-5 p-6">
          {/* Enable-toggle */}
          <label className="flex items-start gap-3 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={saving}
              className="mt-1 h-4 w-4 rounded border-gray-300"
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                BTW Batterij partner
              </p>
              <p className="mt-0.5 text-xs text-gray-500">
                Klanten dienen via /btw/p/slug kosteloos een BTW-teruggave in.
                Bij uitschakelen blijven de instellingen bewaard, maar de
                BTW-partnerpagina werkt niet meer.
              </p>
            </div>
          </label>

          {/* Subsidie-modules */}
          <div className="rounded-xl bg-gray-50 p-4 ring-1 ring-gray-200">
            <p className="text-sm font-medium text-gray-900">
              Subsidie-partner (aanvraag via partner-link, klant betaalt niets)
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              Per regeling aan te zetten. Na goedkeuring komt de aanvraag als
              regel op de partner-factuur voor het bedrag hieronder.
            </p>
            <div className="mt-3 space-y-2">
              {SUBSIDIE_MODULES.map((m) => {
                const on = partnerModules.includes(m);
                return (
                  <div key={m} className="flex flex-wrap items-center gap-3">
                    <label className="flex w-32 items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleModule(m)}
                        disabled={saving}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      {MODULE_LABELS[m]}
                    </label>
                    {on ? (
                      <div className="relative w-40">
                        <span className="absolute inset-y-0 left-3 flex items-center text-xs text-gray-500">
                          €
                        </span>
                        <input
                          type="number"
                          min={0}
                          step={5}
                          value={fees[m]}
                          onChange={(e) =>
                            setFees((f) => ({ ...f, [m]: Number(e.target.value) }))
                          }
                          disabled={saving}
                          className="w-full rounded-md border border-gray-300 px-3 py-1.5 pl-7 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                        <span className="absolute inset-y-0 right-2 flex items-center text-[10px] text-gray-400">
                          excl. btw
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          {anyPartner ? (
            <>
              <label className="block text-sm">
                <span className="font-medium text-gray-900">
                  Partner-slug (URL-segment)
                </span>
                <div className="mt-1 flex items-stretch overflow-hidden rounded-md border border-gray-300 focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500">
                  <span className="flex items-center bg-gray-50 px-2.5 text-xs text-gray-500">
                    {enabled ? "/btw/p/" : "/subsidie/p/"}
                  </span>
                  <input
                    type="text"
                    value={partnerSlug}
                    onChange={(e) => setPartnerSlug(slugify(e.target.value))}
                    disabled={saving}
                    placeholder="solar-centrum-nederland"
                    className="flex-1 px-3 py-2 text-sm focus:outline-none"
                  />
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  Alleen lowercase letters, cijfers en streepjes. Moet uniek
                  zijn.
                </p>
              </label>

              {subsidiePartnerUrl && hasSubsidieModule ? (
                <div className="flex items-center gap-2 rounded-md bg-violet-50 px-3 py-2 text-xs text-violet-900 ring-1 ring-violet-200">
                  <span className="shrink-0 font-medium">Subsidie</span>
                  <span className="flex-1 truncate font-mono">{subsidiePartnerUrl}</span>
                  <button
                    type="button"
                    onClick={() => copyUrl(subsidiePartnerUrl)}
                    className="rounded border border-violet-300 bg-white px-2 py-0.5 font-medium hover:bg-violet-100"
                  >
                    Kopieer
                  </button>
                </div>
              ) : null}

              {partnerUrl && enabled ? (
                <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-900 ring-1 ring-emerald-200">
                  <svg className="h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.71-9.29a1 1 0 00-1.42-1.42L9 10.59 7.71 9.29a1 1 0 00-1.42 1.42l2 2a1 1 0 001.42 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="flex-1 truncate font-mono">{partnerUrl}</span>
                  <button
                    type="button"
                    onClick={() => copyUrl(partnerUrl)}
                    className="rounded border border-emerald-300 bg-white px-2 py-0.5 font-medium hover:bg-emerald-100"
                  >
                    Kopieer
                  </button>
                </div>
              ) : null}

              <label className="block text-sm">
                <span className="font-medium text-gray-900">Logo-URL</span>
                <input
                  type="url"
                  value={partnerLogoUrl}
                  onChange={(e) => setPartnerLogoUrl(e.target.value)}
                  disabled={saving}
                  placeholder="https://.../logo.png"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  Wordt rechts in de header van het partner-formulier getoond.
                </p>
              </label>

              <label className="block text-sm">
                <span className="font-medium text-gray-900">
                  Billing-e-mail (waar partner-facturen heen gaan)
                </span>
                <input
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  disabled={saving}
                  placeholder="facturen@partner.nl"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
              </label>

              {enabled ? (
              <label className="block text-sm">
                <span className="font-medium text-gray-900">
                  BTW-prijs per aanvraag (excl. btw)
                </span>
                <div className="relative mt-1">
                  <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">
                    €
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={5}
                    value={btwPrice}
                    onChange={(e) => setBtwPrice(Number(e.target.value))}
                    disabled={saving}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 pl-7 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  Bedrag dat we per BTW-aanvraag aan de partner factureren.
                </p>
              </label>
              ) : null}
            </>
          ) : null}

          {priceChanged && !saved ? (
            <PriceScopeChoice
              value={priceScope}
              onChange={setPriceScope}
              disabled={saving}
            />
          ) : null}

          {error ? (
            <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              {error}
            </div>
          ) : null}

          {saved ? (
            <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              {anyPartner
                ? `Opgeslagen. ${
                    [enabled ? partnerUrl : null, hasSubsidieModule ? subsidiePartnerUrl : null]
                      .filter(Boolean)
                      .map((u) => `Partner-pagina: ${u}`)
                      .join(" · ")
                  }`
                : "Partner-status uitgezet."}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || (anyPartner && !partnerSlug)}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PartnerConfigModal;
